const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  // ============================================================
  // THEME TOGGLE — Dark / Light mode
  // ============================================================
  const initThemeToggle = () => {
    const toggles = [
      document.getElementById("themeToggle"),
      document.getElementById("themeToggleScroll")
    ].filter(Boolean);
    const html = document.documentElement;
    const STORAGE_KEY = "ox-theme";

    const applyTheme = (theme) => {
      if (theme === "dark") {
        html.setAttribute("data-theme", "dark");
      } else {
        html.removeAttribute("data-theme");
      }
    };

    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      applyTheme(saved);
    } else if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
      applyTheme("dark");
    }

    toggles.forEach((toggle) => {
      toggle.addEventListener("click", () => {
        const isDark = html.hasAttribute("data-theme");
        const next = isDark ? "light" : "dark";
        applyTheme(next);
        localStorage.setItem(STORAGE_KEY, next);
      });
    });
  };

  const onReady = (fn) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
      return;
    }
    fn();
  };

  const formatNumber = (value) => {
    try {
      return new Intl.NumberFormat(undefined).format(value);
    } catch {
      return String(value);
    }
  };

  const typingControllers = new WeakMap();

  const parseTypingWords = (value) => {
    if (!value) return [];
    return value
      .split(",")
      .map((w) => w.trim())
      .filter(Boolean);
  };

  const ensureTyper = (el) => {
    const existing = typingControllers.get(el);
    if (existing) return existing;

    const words = parseTypingWords(el.getAttribute("data-typing-words"));
    const loop = (el.getAttribute("data-typing-loop") ?? "true") !== "false";
    const original = el.textContent ?? "";

    const typeDelay = Number(el.getAttribute("data-typing-type")) || 70;
    const deleteDelay = Number(el.getAttribute("data-typing-delete")) || 45;
    const holdDelay = Number(el.getAttribute("data-typing-hold")) || 900;
    const startDelay = Number(el.getAttribute("data-typing-start")) || 260;

    let running = false;
    let deleting = true;
    let wordIndex = 0;
    let charIndex = (words[0] ?? original).length;
    let timer;

    const setText = (text) => {
      el.textContent = text;
    };

    const getWord = () => words[wordIndex] ?? original;

    const step = () => {
      if (!running) return;

      const word = getWord();

      if (deleting) {
        charIndex = Math.max(0, charIndex - 1);
        setText(word.slice(0, charIndex));

        if (charIndex === 0) {
          deleting = false;
          wordIndex += 1;
          if (wordIndex >= words.length) {
            if (loop) wordIndex = 0;
            else {
              running = false;
              return;
            }
          }
          timer = window.setTimeout(step, startDelay);
          return;
        }

        timer = window.setTimeout(step, deleteDelay);
        return;
      }

      charIndex = Math.min(word.length, charIndex + 1);
      setText(word.slice(0, charIndex));

      if (charIndex >= word.length) {
        deleting = true;
        timer = window.setTimeout(step, holdDelay);
        return;
      }

      timer = window.setTimeout(step, typeDelay);
    };

    const start = () => {
      if (running) return;
      if (prefersReducedMotion || words.length === 0) {
        setText(words[0] ?? original);
        return;
      }

      running = true;
      if (!words[wordIndex]) wordIndex = 0;
      deleting = true;
      charIndex = getWord().length;
      timer = window.setTimeout(step, holdDelay);
    };

    const stop = ({ reset = true } = {}) => {
      running = false;
      if (timer) window.clearTimeout(timer);
      timer = undefined;

      if (!reset) return;

      wordIndex = 0;
      deleting = true;
      charIndex = (words[0] ?? original).length;
      setText(words[0] ?? original);
    };

    const controller = { start, stop };
    typingControllers.set(el, controller);
    return controller;
  };

  const animateCounter = (el) => {
    const target = Number(el.getAttribute("data-counter"));
    if (!Number.isFinite(target)) return;

    const prefix = el.getAttribute("data-prefix") ?? "";
    const suffix = el.getAttribute("data-suffix") ?? "";
    const duration = Number(el.getAttribute("data-duration")) || 900;

    if (prefersReducedMotion) {
      el.textContent = `${prefix}${formatNumber(target)}${suffix}`;
      return;
    }

    const startTime = performance.now();

    const step = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = Math.round(target * eased);
      el.textContent = `${prefix}${formatNumber(current)}${suffix}`;
      if (t < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  };

  const initNavbarScroll = () => {
    const heroNav = document.querySelector(".navbar-hero");
    const scrollNav = document.querySelector(".navbar-oxygen-scroll");
    const navbar = document.querySelector(".navbar-oxygen");
    if (!navbar && !heroNav && !scrollNav) return;

    const update = () => {
      const scrolled = window.scrollY > 10;
      if (heroNav) heroNav.classList.toggle("is-scrolled", scrolled);
      if (scrollNav) scrollNav.classList.toggle("is-visible", scrolled);
      if (navbar && !heroNav && !scrollNav) navbar.classList.toggle("is-scrolled", scrolled);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
  };

  const initSubnav = () => {
    const navbar = document.querySelector(".navbar-oxygen");
    const scrollNav = document.querySelector(".navbar-oxygen-scroll");
    const subnav = document.querySelector(".subnav-oxygen");
    const hero = document.querySelector(".hero-oxygen");
    if (!navbar || !subnav) return;

    const updateTop = () => {
      let top = 0;
      const visibleScrollNav = scrollNav?.classList.contains("is-visible");
      if (visibleScrollNav && scrollNav) {
        top = Math.max(0, Math.round(scrollNav.getBoundingClientRect().bottom));
      } else {
        const rect = navbar.getBoundingClientRect();
        top = Math.max(0, Math.round(rect.bottom));
      }
      subnav.style.setProperty("--ox-subnav-top", `${top}px`);
    };

    const update = () => {
      let heroEnd = 0;
      if (hero) {
        const heroRect = hero.getBoundingClientRect();
        heroEnd = hero.offsetTop + hero.offsetHeight;
      }
      const visible = hero ? window.scrollY > heroEnd : window.scrollY > 10;
      subnav.classList.toggle("is-visible", visible);
      updateTop();
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", updateTop, { passive: true });
  };

  const initBackToTop = () => {
    const btn = document.querySelector(".back-to-top");
    if (!btn) return;

    const update = () => {
      btn.classList.toggle("is-visible", window.scrollY > 650);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });

    btn.addEventListener("click", (e) => {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: prefersReducedMotion ? "auto" : "smooth" });
    });
  };

  const initReveal = () => {
    const elements = Array.from(document.querySelectorAll(".reveal"));
    const orbitCards = Array.from(document.querySelectorAll(".orbit-card"));
    const bentoCells = Array.from(document.querySelectorAll(".bento-cell"));
    const holoCards = Array.from(document.querySelectorAll(".holo-card"));
    const allTargets = [...elements, ...orbitCards, ...bentoCells, ...holoCards];
    if (allTargets.length === 0) return;

    if (!("IntersectionObserver" in window) || prefersReducedMotion) {
      allTargets.forEach((el) => el.classList.add("is-visible", "is-revealed"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          if (entry.isIntersecting) {
            const delay = el.dataset.orbitDelay || el.dataset.bentoDelay || el.dataset.holoDelay;
            if (delay) {
              window.setTimeout(() => el.classList.add("is-revealed"), parseInt(delay, 10));
            } else {
              el.classList.add("is-visible");
            }
          } else {
            el.classList.remove("is-visible", "is-revealed");
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -10% 0px" }
    );

    allTargets.forEach((el) => io.observe(el));
  };

  const initHoloTilt = () => {
    const cards = Array.from(document.querySelectorAll(".holo-card"));
    if (cards.length === 0) return;

    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
    if (isTouchDevice) return;

    cards.forEach((card) => {
      card.addEventListener("mousemove", (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const cx = rect.width / 2;
        const cy = rect.height / 2;

        const rotateX = ((y - cy) / cy) * -12;
        const rotateY = ((x - cx) / cx) * 12;

        card.style.setProperty("--holo-x", `${x}px`);
        card.style.setProperty("--holo-y", `${y}px`);
        card.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`;
      });

      card.addEventListener("mouseleave", () => {
        card.style.transform = "perspective(800px) rotateX(0) rotateY(0) scale(1)";
      });
    });
  };

  const initCounters = () => {
    const counters = Array.from(document.querySelectorAll("[data-counter]"));
    if (counters.length === 0) return;

    if (!("IntersectionObserver" in window)) {
      counters.forEach(animateCounter);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          animateCounter(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.25 }
    );

    counters.forEach((el) => io.observe(el));
  };

  const initOffcanvasClose = () => {
    const offcanvasEl = document.querySelector(".offcanvas");
    if (!offcanvasEl) return;

    let offcanvas;
    try {
      offcanvas = bootstrap.Offcanvas.getOrCreateInstance(offcanvasEl);
    } catch {
      return;
    }

    document.addEventListener("click", (e) => {
      const a = e.target?.closest?.("a.js-close-offcanvas");
      if (!a) return;
      offcanvas.hide();
    });
  };

  const initMegaMenu = () => {
    const toggles = Array.from(document.querySelectorAll("[data-ox-mega-toggle]"));
    if (toggles.length === 0) return;

    const isDesktop = () => window.matchMedia?.("(min-width: 992px)")?.matches;

    const getDropdown = (toggle) => {
      try {
        return bootstrap.Dropdown.getOrCreateInstance(toggle);
      } catch {
        return null;
      }
    };

    toggles.forEach((toggle) => {
      const root = toggle.closest(".dropdown");
      const menu = root?.querySelector(".dropdown-menu");
      if (!root || !menu) return;

      let hideTimer;
      const clearHide = () => {
        if (!hideTimer) return;
        window.clearTimeout(hideTimer);
        hideTimer = undefined;
      };

      const show = () => {
        if (!isDesktop()) return;
        clearHide();
        const dropdown = getDropdown(toggle);
        dropdown?.show?.();
      };

      const hide = () => {
        if (!isDesktop()) return;
        clearHide();
        hideTimer = window.setTimeout(() => {
          const dropdown = getDropdown(toggle);
          dropdown?.hide?.();
        }, 140);
      };

      root.addEventListener("mouseenter", show);
      root.addEventListener("mouseleave", hide);

      root.addEventListener("focusin", show);
      root.addEventListener("focusout", (e) => {
        if (!isDesktop()) return;
        if (root.contains(e.relatedTarget)) return;
        hide();
      });
    });

    const subToggles = Array.from(document.querySelectorAll(".dropdown-submenu > a.has-children"));
    subToggles.forEach((a) => {
      a.addEventListener("click", (e) => {
        e.preventDefault();
        const item = a.closest(".dropdown-submenu");
        if (!item) return;

        const menu = item.closest(".dropdown-menu");
        if (menu) {
          Array.from(menu.querySelectorAll(":scope > .dropdown-submenu.is-open")).forEach((el) => {
            if (el !== item) el.classList.remove("is-open");
          });
        }

        const willOpen = !item.classList.contains("is-open");
        item.classList.toggle("is-open", willOpen);
        a.setAttribute("aria-expanded", willOpen ? "true" : "false");
      });

      a.addEventListener("keydown", (e) => {
        if (e.key !== "Escape") return;
        const item = a.closest(".dropdown-submenu");
        if (!item) return;
        item.classList.remove("is-open");
        a.setAttribute("aria-expanded", "false");
        e.stopPropagation();
      });
    });

    document.addEventListener("hidden.bs.dropdown", () => {
      document.querySelectorAll(".dropdown-submenu.is-open").forEach((el) => el.classList.remove("is-open"));
      document.querySelectorAll(".dropdown-submenu > a.has-children[aria-expanded='true']").forEach((a) => a.setAttribute("aria-expanded", "false"));
    });
  };

  const initActiveNav = () => {
    const links = Array.from(document.querySelectorAll('.subnav-oxygen a.subnav-link[href^="#"], .navbar-oxygen a.nav-link[href^="#"]'));
    if (links.length === 0) return;

    const map = new Map();
    links.forEach((a) => {
      const href = a.getAttribute("href");
      if (!href || href === "#") return;
      const id = href.slice(1);
      const section = document.getElementById(id);
      if (!section) return;
      const existing = map.get(id);
      if (existing) {
        existing.links.push(a);
        return;
      }
      map.set(id, { links: [a], section });
    });

    const entries = Array.from(map.values());
    if (entries.length === 0) return;

    const setActive = (id) => {
      links.forEach((a) => a.classList.remove("active"));
      const item = map.get(id);
      item?.links?.forEach((a) => a.classList.add("active"));
    };

    if (!("IntersectionObserver" in window)) {
      const onScroll = () => {
        let current = entries[0];
        for (const it of entries) {
          const r = it.section.getBoundingClientRect();
          if (r.top <= 120) current = it;
        }
        const id = current.section.id;
        if (id) setActive(id);
      };
      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });
      return;
    }

    const io = new IntersectionObserver(
      (obsEntries) => {
        const visible = obsEntries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0));

        const top = visible[0];
        if (!top?.target?.id) return;
        setActive(top.target.id);
      },
      { threshold: [0.18, 0.28, 0.4, 0.6] }
    );

    entries.forEach((it) => io.observe(it.section));
  };

  const initAnchorOffset = () => {
    const getOffset = () => {
      let offset = 0;
      const scrollNav = document.querySelector(".navbar-oxygen-scroll.is-visible");
      const navbar = scrollNav || document.querySelector(".navbar-oxygen");
      const subnav = document.querySelector(".subnav-oxygen.is-visible");
      if (navbar) offset += Math.round(navbar.getBoundingClientRect().height);
      if (subnav) offset += Math.round(subnav.getBoundingClientRect().height);
      return offset + 12;
    };

    document.addEventListener("click", (e) => {
      const a = e.target?.closest?.('a[href^="#"]');
      if (!a) return;

      const href = a.getAttribute("href");
      if (!href || href === "#") return;

      const id = href.slice(1);
      const target = document.getElementById(id);
      if (!target) return;

      e.preventDefault();
      const top = target.getBoundingClientRect().top + window.scrollY - getOffset();
      window.scrollTo({ top, behavior: prefersReducedMotion ? "auto" : "smooth" });

      const dropdownMenu = a.closest?.(".dropdown-menu");
      const dropdownRoot = dropdownMenu?.closest?.(".dropdown");
      const dropdownToggle = dropdownRoot?.querySelector?.("[data-bs-toggle='dropdown']");
      if (dropdownToggle) {
        try {
          bootstrap.Dropdown.getOrCreateInstance(dropdownToggle).hide();
        } catch {
          // ignore
        }
      }

      history.pushState(null, "", href);
    });
  };

  const initPricingToggle = () => {
    const toggle = document.querySelector("[data-pricing-toggle]");
    if (!toggle) return;

    const prices = Array.from(document.querySelectorAll("[data-price-monthly][data-price-yearly]"));
    const cards = Array.from(document.querySelectorAll(".pricing-card"));
    if (prices.length === 0) return;

    const updatePrices = (yearly) => {
      prices.forEach((el) => {
        el.textContent = yearly ? el.getAttribute("data-price-yearly") : el.getAttribute("data-price-monthly");
      });

      const labels = Array.from(document.querySelectorAll("[data-billing-label]"));
      labels.forEach((el) => {
        el.textContent = yearly ? "per year" : "per month";
      });
    };

    const apply = () => {
      updatePrices(toggle.checked);
    };

    toggle.addEventListener("change", apply);
    updatePrices(toggle.checked);
  };

  const initPricingCardFlip = () => {
    const cards = Array.from(document.querySelectorAll(".pricing-card"));
    if (cards.length === 0) return;

    const isTouchDevice = window.matchMedia("(hover: none)").matches;

    cards.forEach((card) => {
      const flipBtns = card.querySelectorAll(".pricing-card-flip-btn");

      const toggleFlip = () => {
        card.classList.toggle("is-flipped");
      };

      // Flip buttons always work
      flipBtns.forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          toggleFlip();
        });
      });

      // On touch devices: card tap toggles flip
      if (isTouchDevice) {
        card.addEventListener("click", (e) => {
          const isLink = e.target.closest("a[href]");
          const isBtn = e.target.closest("button:not(.pricing-card-flip-btn)");
          if (isLink || isBtn) return;
          toggleFlip();
        });
      }

      // Keyboard: Enter / Space toggles flip
      card.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleFlip();
        }
      });
    });
  };

  const initHeroSlider = () => {
    const root = document.querySelector("[data-hero-slider]");
    if (!root) return;

    const slides = Array.from(root.querySelectorAll("[data-hero-slide]"));
    const dots = Array.from(root.querySelectorAll("[data-hero-dot]"));

    if (slides.length === 0) return;

    let activeIndex = 0;
    let autoTimer;
    let isPaused = false;

    const syncTyping = () => {
      const typingEls = Array.from(root.querySelectorAll(".js-typing[data-typing-words]"));
      if (typingEls.length === 0) return;

      typingEls.forEach((el) => {
        const slide = el.closest("[data-hero-slide]");
        const slideIndex = slide ? Number(slide.getAttribute("data-hero-slide")) : -1;
        const controller = ensureTyper(el);
        if (slideIndex !== activeIndex) controller.stop({ reset: true });
        else controller.start();
      });
    };

    const goTo = (index) => {
      const newIndex = (index + slides.length) % slides.length;
      if (newIndex === activeIndex) return;

      slides.forEach((slide, i) => {
        const isActive = i === newIndex;
        slide.classList.toggle("is-active", isActive);
        if (isActive) {
          slide.querySelectorAll(".reveal").forEach((el) => el.classList.add("is-visible"));
        }
      });

      dots.forEach((dot, i) => {
        dot.classList.toggle("is-active", i === newIndex);
      });

      activeIndex = newIndex;
      syncTyping();
    };

    const next = () => goTo(activeIndex + 1);

    const startAuto = () => {
      if (autoTimer) clearInterval(autoTimer);
      autoTimer = setInterval(() => {
        if (!isPaused) next();
      }, 5000);
    };

    const stopAuto = () => {
      if (autoTimer) {
        clearInterval(autoTimer);
        autoTimer = null;
      }
    };

    dots.forEach((dot) => {
      dot.addEventListener("click", () => {
        const index = Number(dot.getAttribute("data-hero-dot"));
        goTo(index);
        startAuto();
      });
    });

    root.addEventListener("mouseenter", () => { isPaused = true; });
    root.addEventListener("mouseleave", () => { isPaused = false; });

    slides.forEach((slide, i) => {
      if (i === activeIndex) {
        slide.querySelectorAll(".reveal").forEach((el) => el.classList.add("is-visible"));
      }
    });

    syncTyping();
    startAuto();
  };

  const buildVideoEmbedUrl = ({ provider, id, autoplay = false, muted = false, loop = false, controls = 1 } = {}) => {
    if (!provider || !id) return "";

    if (provider === "youtube") {
      const params = new URLSearchParams({
        rel: "0",
        modestbranding: "1",
        playsinline: "1",
        controls: String(controls ? 1 : 0)
      });

      if (autoplay) params.set("autoplay", "1");
      if (muted) params.set("mute", "1");
      if (loop) {
        params.set("loop", "1");
        params.set("playlist", id);
      }

      return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
    }

    if (provider === "vimeo") {
      const params = new URLSearchParams({
        autoplay: autoplay ? "1" : "0",
        muted: muted ? "1" : "0",
        loop: loop ? "1" : "0",
        title: "0",
        byline: "0",
        portrait: "0"
      });
      return `https://player.vimeo.com/video/${id}?${params.toString()}`;
    }

    return "";
  };

  const initVideoDeck = () => {
    const deck = document.querySelector("[data-video-deck]");
    if (!deck) return;

    const cards = Array.from(deck.querySelectorAll("[data-video-card]"));
    if (cards.length === 0) return;

    const modalEl = document.getElementById("videoModal");
    const modalFrame = modalEl?.querySelector?.("[data-video-modal-frame]") ?? null;

    let modal;
    if (modalEl) {
      try {
        modal = bootstrap.Modal.getOrCreateInstance(modalEl);
      } catch {
        modal = undefined;
      }
    }

    const stopPreview = (card) => {
      card.classList.remove("is-playing");
      const preview = card.querySelector(".video-card-preview");
      if (preview) preview.textContent = "";
    };

    const stopAllPreviews = () => {
      cards.forEach(stopPreview);
    };

    const setActive = (card) => {
      cards.forEach((c) => c.classList.toggle("is-active", c === card));
    };

    const startPreview = (card) => {
      if (!card) return;
      if (prefersReducedMotion) {
        card.classList.add("is-playing");
        return;
      }

      const preview = card.querySelector(".video-card-preview");
      if (!preview) return;
      if (preview.querySelector("iframe")) {
        card.classList.add("is-playing");
        return;
      }

      const provider = card.getAttribute("data-video-provider");
      const id = card.getAttribute("data-video-id");
      const src = buildVideoEmbedUrl({ provider, id, autoplay: true, muted: true, loop: true, controls: 0 });
      if (!src) return;

      const iframe = document.createElement("iframe");
      iframe.src = src;
      iframe.allow = "autoplay; encrypted-media; picture-in-picture";
      iframe.allowFullscreen = true;
      iframe.title = "Video preview";
      iframe.loading = "lazy";

      preview.appendChild(iframe);
      card.classList.add("is-playing");
    };

    const openModal = (card) => {
      if (!modal || !modalFrame || !card) return;
      stopAllPreviews();

      const provider = card.getAttribute("data-video-provider");
      const id = card.getAttribute("data-video-id");
      const src = buildVideoEmbedUrl({ provider, id, autoplay: true, muted: false, loop: false, controls: 1 });
      if (!src) return;

      modalFrame.textContent = "";
      const iframe = document.createElement("iframe");
      iframe.src = src;
      iframe.allow = "autoplay; encrypted-media; picture-in-picture";
      iframe.allowFullscreen = true;
      iframe.title = "Video";
      iframe.loading = "lazy";
      modalFrame.appendChild(iframe);
      modal.show();
    };

    cards.forEach((card) => {
      const playBtn = card.querySelector?.("[data-video-play]") ?? null;

      card.addEventListener("mouseenter", () => {
        setActive(card);
        stopAllPreviews();
        startPreview(card);
      });

      card.addEventListener("focus", () => {
        setActive(card);
        stopAllPreviews();
        startPreview(card);
      });

      card.addEventListener("mouseleave", () => {
        stopPreview(card);
      });

      card.addEventListener("blur", () => {
        stopPreview(card);
      });

      card.addEventListener("click", () => {
        setActive(card);
        stopAllPreviews();
        startPreview(card);
      });

      card.addEventListener("keydown", (e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        setActive(card);
        stopAllPreviews();
        startPreview(card);
      });

      if (playBtn) {
        playBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          setActive(card);
          openModal(card);
        });
      }
    });

    if (modalEl && modalFrame) {
      modalEl.addEventListener("hidden.bs.modal", () => {
        modalFrame.textContent = "";
      });
    }
  };

  const initNewsCarousel = () => {
    const track = document.querySelector("[data-news-carousel]");
    if (!track) return;

    const originalCards = Array.from(track.querySelectorAll(".news-carousel-card:not(.is-clone)"));
    if (originalCards.length === 0) return;

    const prevBtn = document.querySelector("[data-news-prev]");
    const nextBtn = document.querySelector("[data-news-next]");

    let autoSlideTimer;
    let isNavigating = false;

    // Clone cards for infinite loop (same count as Top 10)
    const cloneCount = 3;
    const firstCards = originalCards.slice(0, cloneCount);
    const lastCards = originalCards.slice(-cloneCount);

    // Prepend clones of last cards
    lastCards.reverse().forEach(card => {
      const clone = card.cloneNode(true);
      clone.classList.add("is-clone");
      track.insertBefore(clone, track.firstChild);
    });

    // Append clones of first cards
    firstCards.forEach(card => {
      const clone = card.cloneNode(true);
      clone.classList.add("is-clone");
      track.appendChild(clone);
    });

    // Get all cards including clones
    const allCards = Array.from(track.querySelectorAll(".news-carousel-card"));
    const originalCount = originalCards.length;

    const getMetrics = () => {
      const gap = parseInt(getComputedStyle(track).gap || "20", 10) || 20;
      const width = originalCards[0].offsetWidth + gap;
      return {
        cardWidth: width,
        realStart: cloneCount * width,
        realEnd: (cloneCount + originalCount) * width,
        totalWidth: originalCount * width
      };
    };

    const { cardWidth, realStart, realEnd, totalWidth } = getMetrics();

    // Start at first real card, flush left
    track.scrollLeft = realStart;

    const updateSideCards = () => {
      const trackRect = track.getBoundingClientRect();
      const center = trackRect.left + trackRect.width / 2;

      allCards.forEach((card) => {
        if (card.classList.contains("is-clone")) {
          card.classList.add("is-side-card");
          return;
        }

        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(cardCenter - center);

        // If card is more than half its width from center, it's a side card
        if (distance > cardRect.width / 2) {
          card.classList.add("is-side-card");
        } else {
          card.classList.remove("is-side-card");
        }
      });
    };

    const scrollByItems = (delta) => {
      if (isNavigating) return;
      isNavigating = true;

      const m = getMetrics();
      const currentPos = track.scrollLeft;
      const targetPos = currentPos + delta * m.cardWidth;

      if (delta > 0 && targetPos >= m.realEnd) {
        // Wrapping forward: jump to start equivalent without animation
        const offset = targetPos - m.realEnd;
        track.scrollTo({ left: m.realStart + offset, behavior: "auto" });
      } else if (delta < 0 && targetPos < m.realStart) {
        // Wrapping backward: jump to end equivalent without animation
        const offset = m.realStart - targetPos;
        track.scrollTo({ left: m.realEnd - offset, behavior: "auto" });
      } else {
        track.scrollTo({
          left: targetPos,
          behavior: prefersReducedMotion ? "auto" : "smooth"
        });
      }

      resetAutoSlide();
      setTimeout(() => { isNavigating = false; }, prefersReducedMotion ? 50 : 400);
    };

    const autoSlide = () => {
      scrollByItems(1);
    };

    const resetAutoSlide = () => {
      if (autoSlideTimer) {
        window.clearTimeout(autoSlideTimer);
      }
      autoSlideTimer = window.setTimeout(autoSlide, 3000);
    };

    if (prevBtn) {
      prevBtn.addEventListener("click", () => scrollByItems(-1));
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", () => scrollByItems(1));
    }

    // Update side cards on scroll (no checkLoop - explicit wrapping handles it)
    track.addEventListener("scroll", () => {
      if (!prefersReducedMotion) {
        requestAnimationFrame(updateSideCards);
      }
    });

    // Initialize side cards
    updateSideCards();

    // Start auto-slide
    if (!prefersReducedMotion) {
      resetAutoSlide();
    }

    // Pause auto-slide on hover
    track.addEventListener("mouseenter", () => {
      if (autoSlideTimer) {
        window.clearTimeout(autoSlideTimer);
      }
    });

    track.addEventListener("mouseleave", () => {
      if (!prefersReducedMotion) {
        resetAutoSlide();
      }
    });

    // Keyboard navigation
    track.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        scrollByItems(-1);
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        scrollByItems(1);
      }
    });

    // Drag to scroll functionality
    let isDown = false;
    let startX;
    let scrollLeftPos;

    track.addEventListener("mousedown", (e) => {
      isDown = true;
      track.style.cursor = "grabbing";
      startX = e.pageX - track.offsetLeft;
      scrollLeftPos = track.scrollLeft;
    });

    track.addEventListener("mouseleave", () => {
      isDown = false;
      track.style.cursor = "grab";
    });

    track.addEventListener("mouseup", () => {
      isDown = false;
      track.style.cursor = "grab";
      // After drag, silently wrap if user ended up in clone territory
      setTimeout(() => {
        const m = getMetrics();
        if (track.scrollLeft >= m.realEnd) {
          track.scrollLeft = track.scrollLeft - m.totalWidth;
        } else if (track.scrollLeft < m.realStart) {
          track.scrollLeft = track.scrollLeft + m.totalWidth;
        }
      }, 50);
    });

    track.addEventListener("mousemove", (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - track.offsetLeft;
      const walk = (x - startX) * 2;
      track.scrollLeft = scrollLeftPos - walk;
    });

    // Set initial cursor style
    track.style.cursor = "grab";

    // Update side cards on window resize
    window.addEventListener("resize", updateSideCards);

    // Recalculate metrics and clamp scroll on resize
    window.addEventListener("resize", () => {
      const m = getMetrics();
      if (track.scrollLeft < m.realStart - m.cardWidth) {
        track.scrollLeft = m.realStart;
      } else if (track.scrollLeft > m.realEnd + m.cardWidth) {
        track.scrollLeft = m.realStart;
      }
    });
  };

  const initTop10Carousel = () => {
    const track = document.querySelector("[data-top-10-carousel]");
    if (!track) return;

    const originalItems = Array.from(track.querySelectorAll(".top-10-item:not(.is-clone)"));
    if (originalItems.length === 0) return;

    // Clone items for infinite loop
    const cloneCount = 3;
    const firstItems = originalItems.slice(0, cloneCount);
    const lastItems = originalItems.slice(-cloneCount);

    // Prepend clones of last items
    lastItems.reverse().forEach(item => {
      const clone = item.cloneNode(true);
      clone.classList.add("is-clone");
      track.insertBefore(clone, track.firstChild);
    });

    // Append clones of first items
    firstItems.forEach(item => {
      const clone = item.cloneNode(true);
      clone.classList.add("is-clone");
      track.appendChild(clone);
    });

    const getMetrics = () => {
      const gap = parseInt(getComputedStyle(track).gap || "20", 10) || 20;
      const width = originalItems[0].offsetWidth + gap;
      return {
        itemWidth: width,
        realStart: cloneCount * width,
        realEnd: (cloneCount + originalItems.length) * width,
        totalWidth: originalItems.length * width
      };
    };

    const { itemWidth, realStart, realEnd, totalWidth } = getMetrics();

    // Start at first real item, flush left
    track.scrollLeft = realStart;

    let isDown = false;
    let startX;
    let scrollLeftPos;
    let isNavigating = false;

    track.addEventListener("mousedown", (e) => {
      isDown = true;
      track.style.cursor = "grabbing";
      startX = e.pageX - track.offsetLeft;
      scrollLeftPos = track.scrollLeft;
    });

    track.addEventListener("mouseleave", () => {
      isDown = false;
      track.style.cursor = "grab";
    });

    track.addEventListener("mouseup", () => {
      isDown = false;
      track.style.cursor = "grab";
      // After drag, silently wrap if user ended up in clone territory
      setTimeout(() => {
        const m = getMetrics();
        if (track.scrollLeft >= m.realEnd) {
          track.scrollLeft = track.scrollLeft - m.totalWidth;
        } else if (track.scrollLeft < m.realStart) {
          track.scrollLeft = track.scrollLeft + m.totalWidth;
        }
      }, 50);
    });

    track.addEventListener("mousemove", (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - track.offsetLeft;
      const walk = (x - startX) * 2;
      track.scrollLeft = scrollLeftPos - walk;
    });

    // Navigation buttons with explicit wrapping
    const prevBtn = document.querySelector("[data-top-10-prev]");
    const nextBtn = document.querySelector("[data-top-10-next]");

    const scrollByItems = (delta) => {
      if (isNavigating) return;
      isNavigating = true;

      const m = getMetrics();
      const currentPos = track.scrollLeft;
      const targetPos = currentPos + delta * m.itemWidth;

      if (delta > 0 && targetPos >= m.realEnd) {
        // Wrapping forward: jump to start equivalent without animation
        const offset = targetPos - m.realEnd;
        track.scrollTo({ left: m.realStart + offset, behavior: "auto" });
      } else if (delta < 0 && targetPos < m.realStart) {
        // Wrapping backward: jump to end equivalent without animation
        const offset = m.realStart - targetPos;
        track.scrollTo({ left: m.realEnd - offset, behavior: "auto" });
      } else {
        track.scrollTo({
          left: targetPos,
          behavior: prefersReducedMotion ? "auto" : "smooth"
        });
      }

      setTimeout(() => { isNavigating = false; }, prefersReducedMotion ? 50 : 400);
    };

    if (prevBtn) {
      prevBtn.addEventListener("click", () => scrollByItems(-1));
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", () => scrollByItems(1));
    }

    // Set initial cursor style
    track.style.cursor = "grab";

    // Recalculate metrics and clamp scroll on resize
    window.addEventListener("resize", () => {
      const m = getMetrics();
      if (track.scrollLeft < m.realStart - m.itemWidth) {
        track.scrollLeft = m.realStart;
      } else if (track.scrollLeft > m.realEnd + m.itemWidth) {
        track.scrollLeft = m.realStart;
      }
    });
  };

  // ============================================================
  // CARD DECK SHOWCASE — Stacked Interactive Gallery
  // ============================================================
  const initCardDeck = () => {
    const deck = document.getElementById("cardDeck");
    const nav = document.getElementById("deckNav");
    if (!deck) return;

    const cards = Array.from(deck.querySelectorAll(".deck-card"));
    const dots = nav ? Array.from(nav.querySelectorAll(".deck-dot")) : [];
    if (cards.length === 0) return;

    let current = 0;
    let autoPlayTimer = null;

    const update = () => {
      cards.forEach((card, i) => {
        const pos = (i - current + cards.length) % cards.length;
        card.dataset.position = pos;
        card.classList.toggle("is-active", pos === 0);
      });
      dots.forEach((dot, i) => dot.classList.toggle("is-active", i === current));
    };

    const goTo = (index) => {
      current = ((index % cards.length) + cards.length) % cards.length;
      update();
    };

    // Click on a card brings it to front
    cards.forEach((card, i) => {
      card.addEventListener("click", () => {
        goTo(i);
        resetAutoPlay();
      });
    });

    // Dot navigation
    dots.forEach((dot, i) => {
      dot.addEventListener("click", () => {
        goTo(i);
        resetAutoPlay();
      });
    });

    // Keyboard arrows
    deck.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") { goTo(current + 1); resetAutoPlay(); }
      if (e.key === "ArrowLeft") { goTo(current - 1); resetAutoPlay(); }
    });

    // Auto-play every 2s
    const startAutoPlay = () => {
      if (autoPlayTimer) clearInterval(autoPlayTimer);
      autoPlayTimer = setInterval(() => goTo(current + 1), 2000);
    };

    const resetAutoPlay = () => {
      if (autoPlayTimer) clearInterval(autoPlayTimer);
      startAutoPlay();
    };

    // Pause on hover
    deck.addEventListener("mouseenter", () => {
      if (autoPlayTimer) clearInterval(autoPlayTimer);
    });
    deck.addEventListener("mouseleave", startAutoPlay);

    update();
    startAutoPlay();
  };

  const initPreloader = () => {
    const preloader = document.getElementById("pagePreloader");
    if (!preloader) return;

    const hide = () => {
      preloader.classList.add("is-hidden");
      setTimeout(() => {
        preloader.style.display = "none";
      }, 600);
    };

    const start = Date.now();
    const minDisplay = 800;

    const tryHide = () => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, minDisplay - elapsed);
      setTimeout(hide, remaining);
    };

    if (document.readyState === "complete") {
      tryHide();
    } else {
      window.addEventListener("load", tryHide);
    }
  };

  onReady(() => {
    const yearEl = document.getElementById("year");
    if (yearEl) yearEl.textContent = String(new Date().getFullYear());

    initPreloader();
    initNavbarScroll();
    initSubnav();
    initMegaMenu();
    initActiveNav();
    initBackToTop();
    initReveal();
    initHoloTilt();
    initCounters();
    initOffcanvasClose();
    initAnchorOffset();
    initPricingToggle();
    initPricingCardFlip();
    initHeroSlider();
    initVideoDeck();
    initNewsCarousel();
    initTop10Carousel();
    initCardDeck();
    initThemeToggle();
  });
